import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Grid, Chip, Divider,
    CircularProgress, Alert, Stack
} from '@mui/material';
import { Sparkles, CheckCircle, Users, Camera, Tag } from 'lucide-react';
import { getRetreatAccommodations } from '../../../Services/Retreats/RetreatApis';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
        const d = new Date(dateString);
        if (isNaN(d.getTime())) return '—';
        return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
        return '—';
    }
};

// ─── Shared primitives (same design language as SchedulePricingTab) ───────────

/** Section heading with icon */
const SectionTitle = ({ icon: Icon, children }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Icon size={15} color="#FFBD65" strokeWidth={2.2} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E293B', letterSpacing: '0.02em' }}>
            {children}
        </Typography>
    </Box>
);

/**
 * Label → Value row.
 * Stacks vertically on mobile, side-by-side on sm+.
 */
const InfoRow = ({ label, children, noBorder }) => (
    <Box
        sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'flex-start',
            py: 0.9,
            borderBottom: noBorder ? 'none' : '1px solid #F1F5F9',
            gap: { xs: 0.25, sm: 0 },
        }}
    >
        <Typography
            variant="caption"
            sx={{ color: '#94A3B8', fontWeight: 500, minWidth: { sm: 140 }, pt: { sm: '2px' }, flexShrink: 0 }}
        >
            {label}
        </Typography>
        <Box sx={{ flex: 1 }}>
            {typeof children === 'string' || typeof children === 'number' ? (
                <Typography variant="body2" sx={{ fontWeight: 500, color: '#334155' }}>
                    {children}
                </Typography>
            ) : (
                children
            )}
        </Box>
    </Box>
);

/** Thin white card */
const InfoCard = ({ children, sx }) => (
    <Paper
        elevation={0}
        sx={{ border: '1px solid #E8EEF4', borderRadius: '10px', bgcolor: '#FFFFFF', overflow: 'hidden', ...sx }}
    >
        {children}
    </Paper>
);


/** Small status badge */
const StatusBadge = ({ active, trueLabel = 'Enabled ', falseLabel = 'Disabled' }) => (
    <Chip
        label={active ? trueLabel : falseLabel}
        size="small"
        sx={{
            height: 20, fontSize: '0.7rem', fontWeight: 600,
            bgcolor: active ? '#F0FDF4' : '#F8FAFC',
            color: active ? '#16A34A' : '#94A3B8',
            border: `1px solid ${active ? '#BBF7D0' : '#E2E8F0'}`,
        }}
    />
);

const cardPx = { xs: 2, sm: 2.5 };

// ─── Main component ───────────────────────────────────────────────────────────

const AccommodationsTab = ({ retreat, onImageClick }) => {
    const [retreatAccommodations, setRetreatAccommodations] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            if (!retreat?.id) return;
            try {
                setLoading(true);
                const response = await getRetreatAccommodations(retreat.id);
                if (response?.success) {
                    setRetreatAccommodations(response.data);
                } else {
                    setError('Failed to fetch accommodation details.');
                }
            } catch (err) {
                console.error('Error fetching retreat accommodations:', err);
                setError('An error occurred while fetching accommodation information.');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [retreat?.id]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress size={28} sx={{ color: '#FFBD65' }} />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error" sx={{ borderRadius: '8px' }}>{error}</Alert>;
    }

    const accommodationsList = Array.isArray(retreatAccommodations)
        ? retreatAccommodations
        : (retreatAccommodations ? [retreatAccommodations] : []);

    if (accommodationsList.length === 0) {
        return (
            <InfoCard sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                    No accommodation information available.
                </Typography>
            </InfoCard>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {accommodationsList.map((acc, index) => (
                <Box key={acc.id || index} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {accommodationsList.length > 1 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pt: index > 0 ? 2 : 0, pb: 1, borderBottom: '2px solid #E2E8F0' }}>
                            <Chip
                                label={`Option ${index + 1}`}
                                size="small"
                                sx={{ bgcolor: '#FFBD65', color: '#1E293B', fontWeight: 700, fontSize: '0.75rem' }}
                            />
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B' }}>
                                {acc.title || `Accommodation #${index + 1}`}
                            </Typography>
                        </Box>
                    )}

                    {/* ── 1. Overview & Pricing ─────────────────────────────────────── */}
                    <Box>
                        {accommodationsList.length === 1 && (
                            <SectionTitle icon={Sparkles}>Overview & Pricing</SectionTitle>
                        )}
                        <InfoCard>
                            {/* Title + type row */}
                            <Box
                                sx={{
                                    px: cardPx,
                                    py: 1.5,
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    borderBottom: '1px solid #F1F5F9',
                                    gap: 1,
                                }}
                            >
                                <Box>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E293B' }}>
                                        {acc.title || '—'}
                                    </Typography>
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                                        {acc.roomType && (
                                            <Chip
                                                label={acc.roomType}
                                                size="small"
                                                sx={{
                                                    height: 20, fontSize: '0.7rem', textTransform: 'capitalize',
                                                    bgcolor: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE', fontWeight: 600,
                                                }}
                                            />
                                        )}
                                        {acc.priceType && (
                                            <Typography variant="caption" sx={{ color: '#94A3B8', textTransform: 'capitalize' }}>
                                                {acc.priceType.replace('_', ' ')}
                                            </Typography>
                                        )}
                                    </Stack>
                                </Box>
                                <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#1E293B', lineHeight: 1.2 }}>
                                        {acc.currency} {acc.price}
                                    </Typography>
                                    {acc.setDeposit && acc.depositPercentage && (
                                        <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                                            Deposit Percentage: {acc.depositPercentage}%
                                        </Typography>
                                    )}
                                </Box>
                            </Box>

                            {/* Detail rows */}
                            <Box sx={{ px: cardPx, py: 1.5 }}>
                                <Grid container>
                                    <Grid size={{ xs: 12, md: 6 }} sx={{ pr: { md: 3 }, borderRight: { md: '1px solid #F1F5F9' } }}>
                                        <InfoRow label="Room Type">{acc.roomType ?? '—'}</InfoRow>
                                        <InfoRow label="Price Type">
                                            {acc.priceType ? acc.priceType.replace('_', ' ') : '—'}
                                        </InfoRow>
                                        <InfoRow label="Max Occupancy">
                                            {acc.maxOccupancy ? `${acc.maxOccupancy} person(s)` : '—'}
                                        </InfoRow>
                                        <InfoRow label="Deposit Required" noBorder>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                <StatusBadge active={acc.depositRequired} />
                                                {acc.setDeposit && acc.depositPercentage && (
                                                    <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                                                        ({acc.depositPercentage}%)
                                                    </Typography>
                                                )}
                                            </Box>
                                        </InfoRow>
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 6 }} sx={{ pl: { md: 3 }, borderTop: { xs: '1px solid #F1F5F9', md: 'none' }, pt: { xs: 1, md: 0 } }}>
                                        <InfoRow label="Early Bird Price">
                                            {acc.earlyBirdPrice
                                                ? <Typography variant="body2" sx={{ fontWeight: 600, color: '#B45309' }}>{acc.currency} {acc.earlyBirdPrice}</Typography>
                                                : '—'
                                            }
                                        </InfoRow>
                                        <InfoRow label="Flash Sale Price">
                                            {acc.flashSalePrice
                                                ? <Typography variant="body2" sx={{ fontWeight: 600, color: '#0E7490' }}>{acc.currency} {acc.flashSalePrice}</Typography>
                                                : '—'
                                            }
                                        </InfoRow>
                                        <InfoRow label="Set Deposit">
                                            <StatusBadge active={acc.setDeposit} />
                                        </InfoRow>
                                        <InfoRow label="Accommodation ID" noBorder>
                                            {acc.id ?? '—'}
                                        </InfoRow>
                                    </Grid>
                                </Grid>
                            </Box>

                            {/* Description */}
                            {acc.description && (
                                <>
                                    <Divider />
                                    <Box sx={{ px: cardPx, py: 1.5 }}>
                                        <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: 1 }}>
                                            Description
                                        </Typography>
                                        <Box
                                            sx={{
                                                color: '#475569', fontSize: '0.85rem', lineHeight: 1.65,
                                                '& p': { mb: 1 },
                                                '& *': { fontFamily: 'inherit !important', fontSize: 'inherit !important', color: 'inherit !important' },
                                            }}
                                            dangerouslySetInnerHTML={{ __html: acc.description }}
                                        />
                                    </Box>
                                </>
                            )}
                        </InfoCard>
                    </Box>

                    {/* ── 2. Facilities & Amenities ─────────────────────────────────── */}
                    {acc.facilities?.length > 0 && (
                        <Box>
                            <SectionTitle icon={Sparkles}>Facilities & Amenities</SectionTitle>
                            <InfoCard>
                                <Box sx={{ px: cardPx, py: 2 }}>
                                    <Stack direction="row" flexWrap="wrap" gap={1}>
                                        {acc.facilities.map((facility) => (
                                            <Chip
                                                key={facility.id}
                                                label={facility.facilityName}
                                                size="small"
                                                icon={<CheckCircle size={13} color="#16A34A" />}
                                                sx={{
                                                    height: 26,
                                                    fontSize: '0.75rem',
                                                    fontWeight: 500,
                                                    bgcolor: '#F0FDF4',
                                                    color: '#166534',
                                                    border: '1px solid #BBF7D0',
                                                    borderRadius: '6px',
                                                }}
                                            />
                                        ))}
                                    </Stack>
                                </Box>
                            </InfoCard>
                        </Box>
                    )}

                    {/* ── 3. Availability / Open Spots ──────────────────────────────── */}
                    {acc.openSpots?.length > 0 && (
                        <Box>
                            <SectionTitle icon={Users}>Availability & Slots</SectionTitle>
                            <InfoCard>
                                {/* Table header */}
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr auto', sm: '1fr 1fr auto' },
                                        px: cardPx, py: 1,
                                        bgcolor: '#F8FAFC',
                                        borderBottom: '1px solid #F1F5F9',
                                        gap: 2,
                                    }}
                                >
                                    <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        Period
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', display: { xs: 'none', sm: 'block' } }}>
                                        Max Occupancy
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'right' }}>
                                        Spots Left
                                    </Typography>
                                </Box>

                                {acc.openSpots.map((spot, idx) => {
                                    const left = (spot.defaultOpenSpots ?? 0) - (spot.bookedSpots ?? 0);
                                    const isAvailable = left > 0;
                                    return (
                                        <Box key={spot.id}>
                                            <Box
                                                sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns: { xs: '1fr auto', sm: '1fr 1fr auto' },
                                                    px: cardPx, py: 1.1,
                                                    gap: 2,
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <Box>
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B' }}>
                                                        {spot.startDate && spot.endDate
                                                            ? `${formatDate(spot.startDate)} → ${formatDate(spot.endDate)}`
                                                            : 'Always Available'
                                                        }
                                                    </Typography>
                                                    {/* Mobile: show occupancy below date */}
                                                    <Typography variant="caption" sx={{ color: '#94A3B8', display: { xs: 'block', sm: 'none' } }}>
                                                        Max occupancy: {spot.maxOccupancy ?? '—'}
                                                    </Typography>
                                                </Box>
                                                <Typography variant="body2" sx={{ color: '#64748B', display: { xs: 'none', sm: 'block' } }}>
                                                    {spot.maxOccupancy ?? '—'}
                                                </Typography>
                                                <Box sx={{ textAlign: 'right' }}>
                                                    <Chip
                                                        label={`${left} / ${spot.defaultOpenSpots ?? 0}`}
                                                        size="small"
                                                        sx={{
                                                            height: 20, fontSize: '0.7rem', fontWeight: 600,
                                                            bgcolor: isAvailable ? '#F0FDF4' : '#FFF1F2',
                                                            color: isAvailable ? '#16A34A' : '#E11D48',
                                                            border: `1px solid ${isAvailable ? '#BBF7D0' : '#FECDD3'}`,
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                            {idx < acc.openSpots.length - 1 && <Divider />}
                                        </Box>
                                    );
                                })}
                            </InfoCard>
                        </Box>
                    )}

                    {/* ── 4. Seasonal Pricing ───────────────────────────────────────── */}
                    {acc.seasonalPrices?.length > 0 && (
                        <Box>
                            <SectionTitle icon={Tag}>Seasonal Pricing</SectionTitle>
                            <InfoCard>
                                {/* Table header */}
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr auto',
                                        px: cardPx, py: 1,
                                        bgcolor: '#F8FAFC',
                                        borderBottom: '1px solid #F1F5F9',
                                        gap: 2,
                                    }}
                                >
                                    <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Period</Typography>
                                    <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Price</Typography>
                                </Box>

                                {acc.seasonalPrices.map((season, idx) => (
                                    <Box key={season.id}>
                                        <Box
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: '1fr auto',
                                                px: cardPx, py: 1.1,
                                                gap: 2,
                                                alignItems: 'center',
                                                bgcolor: idx % 2 === 0 ? '#FFFFFF' : '#FAFBFC',
                                            }}
                                        >
                                            <Typography variant="body2" sx={{ color: '#475569', fontWeight: 500 }}>
                                                {formatDate(season.startDate)} → {formatDate(season.endDate)}
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#1E293B', whiteSpace: 'nowrap' }}>
                                                {acc.currency} {season.price}
                                            </Typography>
                                        </Box>
                                        {idx < acc.seasonalPrices.length - 1 && <Divider />}
                                    </Box>
                                ))}
                            </InfoCard>
                        </Box>
                    )}

                    {/* ── 5. Gallery ────────────────────────────────────────────────── */}
                    {acc.images?.length > 0 && (
                        <Box>
                            <SectionTitle icon={Camera}>Accommodation Gallery</SectionTitle>
                            <Grid container spacing={1.5}>
                                {acc.images.map((img) => (
                                    <Grid size={{ xs: 6, sm: 4, md: 3 }} key={img.id}>
                                        <Box
                                            component="img"
                                            loading="lazy"
                                            src={img.imageUrl}
                                            alt="Accommodation"
                                            onClick={() => {
                                                if (onImageClick) {
                                                    onImageClick(img.imageUrl);
                                                } else {
                                                    window.open(img.imageUrl, '_blank');
                                                }
                                            }}
                                            sx={{
                                                width: '100%',
                                                height: { xs: 100, sm: 120, md: 140 },
                                                objectFit: 'cover',
                                                borderRadius: '8px',
                                                border: '1px solid #E8EEF4',
                                                cursor: 'pointer',
                                                display: 'block',
                                            }}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    )}

                </Box>
            ))}
        </Box>
    );
};

export default AccommodationsTab;
